import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PumaResult } from '../services/pumsTypes';
import { getPumaCodesForRegion, getRegionCenter, getRegionZoom } from '../services/regionMapping';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface BayAreaCountyMapProps {
  pumaResults: PumaResult[];
  onPumaClick?: (pumaCode: number) => void;
  loading?: boolean;
  selectedRegion?: number;
  useAllPumas?: boolean; // If true, match all PUMAs from pumaResults (for California data), not just selected region
}

const BayAreaCountyMap: React.FC<BayAreaCountyMapProps> = ({ 
  pumaResults, 
  onPumaClick, 
  loading = false,
  selectedRegion = 3,
  useAllPumas = false // Default to false (region-filtered) for backward compatibility
}) => {
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [mapLoading, setMapLoading] = useState(true);

  // Load PUMA GeoJSON
  useEffect(() => {
    const loadGeoJson = async () => {
      try {
        const response = await fetch(`/data/california_pumas_2023.geojson?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          setGeoJsonData(data);
          console.log('🗺️ Loaded PUMA GeoJSON with', data.features?.length, 'features');
        }
      } catch (error) {
        console.error('Error loading PUMA GeoJSON:', error);
      } finally {
        setMapLoading(false);
      }
    };

    loadGeoJson();
  }, []);

  // Get PUMA data for a specific PUMA code
  const getPumaData = useCallback((pumaCode: number): PumaResult | undefined => {
    return pumaResults.find(puma => puma.puma === pumaCode);
  }, [pumaResults]);

  // Get PUMA ranking
  const getPumaRanking = useCallback((pumaCode: number): number => {
    const sortedResults = [...pumaResults].sort((a, b) => b.targetHouseholds - a.targetHouseholds);
    const index = sortedResults.findIndex(puma => puma.puma === pumaCode);
    return index >= 0 ? index + 1 : 0;
  }, [pumaResults]);

  // Map GeoJSON PUMA code to Supabase PUMA code
  const mapGeoJsonPumaToSupabase = useCallback((geojsonPuma: string): number | null => {
    // If useAllPumas is true, match against all PUMAs in pumaResults (for California data)
    // Otherwise, match against selected region PUMAs (for Bay Area data)
    const availablePumaCodes = useAllPumas 
      ? pumaResults.map(p => p.puma) // All PUMAs from results
      : getPumaCodesForRegion(selectedRegion); // Region-filtered PUMAs
    
    // Debug logging for problematic PUMA codes
    if (geojsonPuma === '00101' || geojsonPuma === '00113') {
      console.log(`🔍 Debugging PUMA ${geojsonPuma}:`, {
        geojsonPuma,
        useAllPumas,
        availablePumaCodes: availablePumaCodes.slice(0, 10),
        totalAvailable: availablePumaCodes.length,
        includesDirect: availablePumaCodes.includes(parseInt(geojsonPuma))
      });
    }
    
    // Strategy 1: Direct match with parsed integer
    const asInt = parseInt(geojsonPuma);
    if (!isNaN(asInt) && availablePumaCodes.includes(asInt)) {
      return asInt;
    }
    
    // Strategy 2: Match by removing leading zeros from GeoJSON and comparing with available codes
    const withoutLeadingZeros = parseInt(geojsonPuma.replace(/^0+/, ''));
    if (!isNaN(withoutLeadingZeros) && availablePumaCodes.includes(withoutLeadingZeros)) {
      return withoutLeadingZeros;
    }
    
    // Strategy 3: Try matching by padding available codes with leading zeros (3, 4, 5, 6 digits)
    // This handles cases where GeoJSON has "00101" and Supabase has 101
    for (const availablePuma of availablePumaCodes) {
      const availablePumaStr = availablePuma.toString();
      
      // Try different padding lengths (3, 4, 5, 6 digits) to match GeoJSON format
      for (let padLength = 3; padLength <= 6; padLength++) {
        const paddedPuma = availablePumaStr.padStart(padLength, '0');
        if (geojsonPuma === paddedPuma || geojsonPuma === availablePumaStr) {
          return availablePuma;
        }
      }
      
      // Also try matching the string directly (in case one is string and one is number)
      if (geojsonPuma === availablePumaStr) {
        return availablePuma;
      }
    }
    
    // Strategy 4: Try removing leading zeros from GeoJSON and matching with different padding
    // Handle cases where GeoJSON is "060101" and Supabase is 101 (need to remove state prefix)
    const geojsonWithoutZeros = geojsonPuma.replace(/^0+/, '');
    if (geojsonWithoutZeros.length > 0) {
      const geojsonInt = parseInt(geojsonWithoutZeros);
      if (!isNaN(geojsonInt) && availablePumaCodes.includes(geojsonInt)) {
        return geojsonInt;
      }
      
      // Also try matching as string with different padding
      for (let padLength = 3; padLength <= 6; padLength++) {
        const paddedGeojson = geojsonWithoutZeros.padStart(padLength, '0');
        for (const availablePuma of availablePumaCodes) {
          const paddedAvailable = availablePuma.toString().padStart(padLength, '0');
          if (paddedGeojson === paddedAvailable) {
            return availablePuma;
          }
        }
      }
    }
    
    // Strategy 5: Last resort - try extracting PUMA from 6-digit codes (remove state prefix)
    // Some GeoJSON might have "060101" where "60" is state FIPS and "0101" is PUMA
    if (geojsonPuma.length === 6 && geojsonPuma.startsWith('06')) {
      const pumaPart = geojsonPuma.slice(2); // Remove "06" state prefix
      const pumaInt = parseInt(pumaPart);
      if (!isNaN(pumaInt) && availablePumaCodes.includes(pumaInt)) {
        return pumaInt;
      }
      // Also try with leading zeros removed
      const pumaWithoutZeros = parseInt(pumaPart.replace(/^0+/, ''));
      if (!isNaN(pumaWithoutZeros) && availablePumaCodes.includes(pumaWithoutZeros)) {
        return pumaWithoutZeros;
      }
    }
    
    // No match found - return null (will show "no matching data" on map)
    return null;
  }, [selectedRegion, useAllPumas, pumaResults]);

  // Memoize color breaks calculation (only recalculate when pumaResults changes)
  const colorBreaks = useMemo(() => {
    if (!pumaResults || pumaResults.length === 0) return [0, 0, 0, 0, 0, 0];
    
    // Get all target household values, excluding zeros
    const values = pumaResults
      .map(puma => puma.targetHouseholds)
      .filter(value => value > 0)
      .sort((a, b) => a - b);
    
    if (values.length === 0) return [0, 0, 0, 0, 0, 0];
    
    // Calculate percentiles for natural breaks
    const breaks = [
      values[Math.floor(values.length * 0.2)], // 20th percentile
      values[Math.floor(values.length * 0.4)], // 40th percentile  
      values[Math.floor(values.length * 0.6)], // 60th percentile
      values[Math.floor(values.length * 0.8)], // 80th percentile
      values[Math.floor(values.length * 0.95)] // 95th percentile
    ];
    
    // Ensure breaks are unique and increasing
    const uniqueBreaks = Array.from(new Set(breaks)).sort((a, b) => a - b);
    
    // Pad with zeros if needed
    while (uniqueBreaks.length < 5) {
      uniqueBreaks.push(uniqueBreaks[uniqueBreaks.length - 1] || 0);
    }
    
    return uniqueBreaks;
  }, [pumaResults]);

  // Style function for GeoJSON features with dynamic color grading (memoized with cached breaks)
  const getStyle = useCallback((feature: any) => {
    const geojsonPuma = feature.properties.PUMACE20 || feature.properties.PUMA || feature.properties.GEOID || '0'; // Prioritize PUMACE20 for 2023 data
    const pumaCode = mapGeoJsonPumaToSupabase(geojsonPuma);
    const pumaData = pumaCode ? getPumaData(pumaCode) : null;
    const targetHouseholds = pumaData ? pumaData.targetHouseholds : 0;
    
    // Use memoized color breaks (no recalculation)
    const breaks = colorBreaks;
    
    // Color ramp from light yellow to dark red based on dynamic breaks
    let color = '#f0f0f0'; // Default gray for no data
    
    if (targetHouseholds > 0) {
      if (targetHouseholds >= breaks[4]) color = '#8B0000'; // Dark red (top 5%)
      else if (targetHouseholds >= breaks[3]) color = '#DC143C'; // Crimson (top 20%)
      else if (targetHouseholds >= breaks[2]) color = '#FF4500'; // Orange red (top 40%)
      else if (targetHouseholds >= breaks[1]) color = '#FFA500'; // Orange (top 60%)
      else if (targetHouseholds >= breaks[0]) color = '#FFD700'; // Gold (top 80%)
      else color = '#FFFF00'; // Yellow (bottom 20%)
    }

    return {
      fillColor: color,
      weight: 1,
      opacity: 1,
      color: '#666',
      dashArray: '',
      fillOpacity: 0.7
    };
  }, [getPumaData, mapGeoJsonPumaToSupabase, colorBreaks]);

  // Handle feature click
  const onEachFeature = useCallback((feature: any, layer: any) => {
    const geojsonPuma = feature.properties.PUMACE20 || feature.properties.PUMA || feature.properties.GEOID || '0'; // Prioritize PUMACE20 for 2023 data
    const pumaCode = mapGeoJsonPumaToSupabase(geojsonPuma);
    
    if (!pumaCode) {
      // Get the actual name from GeoJSON for no-matching-data case
      const rawName = feature.properties.NAMELSAD20 || feature.properties.NAME || `PUMA ${geojsonPuma}`;
      const cleanName = rawName.replace(/ PUMA$/, '').replace(/--/g, ' - ');
      layer.bindTooltip(`${cleanName}<br/>No matching data`);
      return;
    }
    
    const pumaData = getPumaData(pumaCode);
    const ranking = getPumaRanking(pumaCode);
    // Create tooltip using actual GeoJSON names
    if (pumaData) {
      // Get the actual name from GeoJSON, clean it up
      const rawName = feature.properties.NAMELSAD20 || feature.properties.NAME || `PUMA ${geojsonPuma}`;
      const cleanName = rawName.replace(/ PUMA$/, '').replace(/--/g, ' - ');
      
      const tooltipContent = `
        <div style="font-size: 12px; line-height: 1.4;">
          <strong>${cleanName}</strong><br/>
          <strong>County:</strong> ${pumaData.county}<br/>
          <strong>PUMA Code:</strong> ${pumaCode.toString().padStart(5, '0')}<br/>
          <strong>Target Households:</strong> ${pumaData.targetHouseholds.toLocaleString()}<br/>
          <strong>Total Households:</strong> ${pumaData.totalHouseholds.toLocaleString()}<br/>
          <strong>Target %:</strong> ${pumaData.targetPercentage.toFixed(1)}%<br/>
          <strong>Rank:</strong> #${ranking}
        </div>
      `;
      layer.bindTooltip(tooltipContent);
    } else {
      // Get the actual name from GeoJSON for no-data case too
      const rawName = feature.properties.NAMELSAD20 || feature.properties.NAME || `PUMA ${geojsonPuma}`;
      const cleanName = rawName.replace(/ PUMA$/, '').replace(/--/g, ' - ');
      layer.bindTooltip(`${cleanName}<br/>No data available`);
    }

    // Handle click
    layer.on('click', () => {
      if (onPumaClick) {
        onPumaClick(pumaCode);
      }
    });
  }, [getPumaData, getPumaRanking, onPumaClick, mapGeoJsonPumaToSupabase]);

  if (mapLoading || loading) {
    return (
      <div className="map-container" style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading map...</div>
      </div>
    );
  }

  if (!geoJsonData) {
    return (
      <div className="map-container" style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Error loading map data</div>
      </div>
    );
  }

  // Calculate center and zoom based on selected region
  // If "All California" is selected (region 0), use California-wide center/zoom
  // Otherwise, use region-specific center/zoom
  const effectiveRegion = useAllPumas ? 0 : selectedRegion;
  const mapCenter: [number, number] = getRegionCenter(effectiveRegion);
  const mapZoom = getRegionZoom(effectiveRegion);

  return (
    <div className="map-container">
      <MapContainer
        key={`region-${effectiveRegion}`} // Force re-render when region changes
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '500px', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        <GeoJSON
          data={geoJsonData}
          style={getStyle}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
      
      {/* Dynamic Legend */}
      <div className="map-legend" style={{ marginTop: '10px', fontSize: '12px' }}>
        <h4>Target Households by PUMA (Dynamic Scale)</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {(() => {
            const colors = ['#FFFF00', '#FFD700', '#FFA500', '#FF4500', '#DC143C', '#8B0000'];
            const labels = ['Lowest', 'Low', 'Medium', 'High', 'Very High', 'Highest'];
            
            return colors.map((color, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '15px', backgroundColor: color }}></div>
                <span>{labels[index]}</span>
              </div>
            ));
          })()}
        </div>
        <div style={{ marginTop: '5px', fontSize: '10px', color: '#666' }}>
          Colors adjust automatically based on data distribution
        </div>
      </div>
    </div>
  );
};

export default BayAreaCountyMap;